import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import StyledTable from "src/components/Table";
import { GridColDef } from "@mui/x-data-grid";
import { AppDispatch, RootState } from "src/store";
import { deleteorder, getorders, reset } from "../redux/orderSlice";
import { IconButton, Box, Typography } from "@mui/material";
import { Add, Eye } from "iconsax-react";
import colors from "src/theme/colors";
import styled from "@emotion/styled";
import { StyledButton } from "src/components/Button";
import ConfirmModal from "src/components/Modal";
import { PageTemplate } from "src/layout/components/PageTemplate";
import { ListBoundary } from "src/components/ListBoundary/ListBoundary";

const orderTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [page, setPage] = useState(1);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [ordersData, setordersData] = useState([] as any);
  const [ordersMetaData, setordersMetaData] = useState([] as any);
  const navigate = useNavigate();
  const [selected, setSelected] = useState([] as any);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState([] as any);

  const {
    orders,
    ordersMeta,
    isError,
    isDeleteError,
    isSuccess,
    isDeleteSuccess,
    isDeleteLoading,
    message,
    deleteMessage,
    isLoading,
  } = useSelector((state: RootState) => state.orders);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(message, { variant: "error" });
    }
    if (isSuccess) {
      if (message) {
        enqueueSnackbar(message, { variant: "success" });
      } else {
        setordersData(orders);
        setordersMetaData(ordersMeta);
      }
    }
    return () => {
      dispatch(reset());
    };
  }, [isError, isSuccess, message, page]);

  useEffect(() => {
    if (isDeleteError) {
      enqueueSnackbar(deleteMessage, { variant: "error" });
    }
    if (isDeleteSuccess) {
      enqueueSnackbar(deleteMessage, { variant: "success" });
      dispatch(
        getorders({
          page:
            ordersData.length <= 1 || selectedId.length >= ordersData.length
              ? page > 1
                ? page - 1
                : 1
              : page,
        })
      ).then(() => setIsDeleteModalOpen(false));

      if (ordersData.length <= 1 || selectedId.length >= ordersData.length) {
        setPage(page > 1 ? page - 1 : 1);
      }
      setSelected([]);
      setSelectedId([]);
    }
    return () => {
      dispatch(reset());
    };
  }, [isDeleteError, isDeleteSuccess, deleteMessage]);

  useEffect(() => {
    dispatch(
      getorders({
        page: page,
      })
    );
    setSelected([]);
    setSelectedId([]);
  }, [page]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
      filterable: false,
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "code",
      headerName: "Order Id",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "vendor",
      headerName: "Vendor",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Box padding={2} color={"#1E2329"}>
          {params.row?.vendor?.code}
        </Box>
      ),
    },
    {
      field: "material",
      headerName: "Material",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box padding={2} color={"#1E2329"}>
          {params.row?.material?.name}
        </Box>
      ),
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: "Warehouse",
      headerName: "Warehouse",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box padding={2} color={"#1E2329"}>
          {params.row?.warehouse?.title}
        </Box>
      ),
    },
    {
      field: "Actions",
      headerName: "Actions",
      flex: 0.4,
      minWidth: 140,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="space-around"
          alignItems="center"
          flexDirection="row"
        >
          <IconButton
            onClick={() =>
              navigate("/orders/order-information", {
                state: { id: params?.id },
              })
            }
          >
            <Eye color={colors.gray[600]} size="24" />
          </IconButton>
          <IconButton
            onClick={() =>
              navigate("/orders/order-edit", {
                state: { orderData: params.row },
              })
            }
          >
            <img
              src={process.env.PUBLIC_URL + "/assets/icons/Edit.svg"}
              alt="edit"
            />
          </IconButton>
          {/* <IconButton>
            <img
              src={process.env.PUBLIC_URL + "/assets/icons/Remove.svg"}
              alt="delete"
              onClick={() => {
                setSelectedId([Number(params?.id)]);
                setIsDeleteModalOpen(true);
              }}
            />
          </IconButton> */}
        </Box>
      ),
    },
  ];

  const onDelete = (selected: any) => {
    const deleteValue = {
      order_ids: selected,
    };
    dispatch(deleteorder(deleteValue));
  };

  return (
    <PageTemplate
      crumbs={[
        {
          label: "orders list",
        },
      ]}
    >
      <ListBoundary
        isEmpty={ordersData.length === 0 || !ordersData}
        isDataLoading={isLoading}
        fallbackImageURL="/assets/images/emptyStates/Orders-List EmptyState.svg"
        description="Register Your First Purchase Order!"
        buttonLabel="New Purchase Order"
        onClick={() => navigate("/orders/order-new")}
      >
        <StyledHeader>
          <StyledButton
            variant="contained"
            endIcon={<Add size="24" color={colors.gray[850]} />}
            onClick={() => navigate("/orders/order-new")}
          >
            New Order
          </StyledButton>
          {/* {selected.length !== 0 ? (
            <StyledButton
              variant="contained"
              endIcon={
                <img
                  src={process.env.PUBLIC_URL + "/assets/icons/removeWhite.svg"}
                  alt="delete"
                />
              }
              onClick={() => {
                setSelectedId(selected);
                setIsDeleteModalOpen(true);
              }}
              color="error"
            >
              Remove Orders
            </StyledButton>
          ) : (
            <StyledButton
              variant="contained"
              endIcon={<Add size="24" color={colors.gray[850]} />}
              onClick={() => navigate("/orders/order-new")}
            >
              New Order
            </StyledButton>
          )} */}
        </StyledHeader>
        <StyledTable
          disableColumnMenu
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableVirtualization
          disableSelectionOnClick
          loading={isLoading}
          checkboxSelection
          experimentalFeatures={{ newEditingApi: true }}
          rows={ordersData}
          columns={columns}
          onSelectionModelChange={(e) => setSelected(e)}
          onPageChange={(e) => setPage(e + 1)}
          page={page - 1}
          paginationMode="server"
          rowCount={ordersMetaData?.pagination?.total_items}
        />
      </ListBoundary>
      <ConfirmModal
        title={"Remove orders"}
        iconName={"ModalRemove"}
        open={isDeleteModalOpen}
        isLoading={isDeleteLoading || isLoading}
        onDismiss={() => setIsDeleteModalOpen(false)}
        onConfirm={() => onDelete(selectedId)}
        confrimText={"Remove"}
        cancelText={"Cancel"}
      >
        <Typography fontSize={14} fontWeight={400} color={colors.gray[450]}>
          Are you sure you want to remove these orders?
        </Typography>
      </ConfirmModal>
    </PageTemplate>
  );
};

const StyledHeader = styled(Box)`
  display: flex;
  flex-direction: row-reverse;
  padding-bottom: 24px;
`;

export default orderTable;
